import { Col, Divider, Row } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdBusiness, MdCampaign, MdPayment } from "react-icons/md";
import { FaDonate, FaHandsHelping } from "react-icons/fa";
import { GiReceiveMoney } from "react-icons/gi";
import Spin from "../../components/general/antd/spin";
import PageHeader from "../../components/general/layout/page-header";
import Card from "../../components/home/cards";
import EndPoints from "../../services/end-points";
import { IStatistics } from "../../models/statistcs/respons";
import RefreshBtn from "../../components/general/table-components/actions/refresh-btn";

const Home: React.FC = () => {
  const { t } = useTranslation();
  const [statistics, setStatistics] = useState<IStatistics>();
  const [loading, setLoading] = useState(true);

  const getAllStatistics = async () => {
    try {
      setLoading(true);
      const response = await EndPoints.statistics.getAllStatistics({});
      setStatistics(response?.data);
    } catch (error) {
      console.error("Error fetching statistics:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllStatistics();
  }, []);

  return (
    <>
      <PageHeader
        title={t("home_title")}
        extra={[
          <RefreshBtn key={0} loading={loading} onClick={getAllStatistics}>
            {t("refresh")}
          </RefreshBtn>,
        ]}
      />

      <Spin spinning={loading}>
        <Row gutter={[8, 8]}>
          <Col xl={8} lg={12} xs={24}>
            <Card
              toolTip="charities_count"
              title="charities_count"
              icon={<MdBusiness />}
              count={statistics?.charities}
              details={[
                {
                  label: "active",
                  value: statistics?.charitiesByStats["1"] || 0,
                },
                {
                  label: "inactive",
                  value: statistics?.charitiesByStats["2"] || 0,
                  colorType: "secondary",
                },
              ]}
              chartData={{
                labels: [t("active"), t("inactive")],
                data: [
                  statistics?.charitiesByStats["1"] || 0,
                  statistics?.charitiesByStats["2"] || 0,
                ],
                colors: ["#0d4ea2", "#e0e0e0"],
              }}
            />
          </Col>

          <Col xl={8} lg={12} xs={24}>
            <Card
              toolTip="campaigns_count"
              title="campaigns_count"
              icon={<MdCampaign />}
              count={statistics?.campaigns}
              details={[
                {
                  label: "not_started",
                  value: statistics?.campaignsByStats["1"] || 0,
                },
                {
                  label: "collecting_donations",
                  value: statistics?.campaignsByStats["2"] || 0,
                  colorType: "secondary",
                },
                {
                  label: "finished",
                  value: statistics?.campaignsByStats["3"] || 0,
                  colorType: "tertiary",
                },
              ]}
              chartData={{
                labels: [
                  t("not_started"),
                  t("collecting_donations"),
                  t("finished"),
                ],
                data: [
                  statistics?.campaignsByStats["1"] || 0,
                  statistics?.campaignsByStats["2"] || 0,
                  statistics?.campaignsByStats["3"] || 0,
                ],
                colors: ["#0d4ea2", "#5a9ef7", "#e0e0e0"],
              }}
            />
          </Col>

          <Col xl={8} lg={12} xs={24}>
            <Card
              toolTip="campaigns_by_goal"
              title="campaigns_by_goal"
              icon={<GiReceiveMoney />}
              details={[
                {
                  label: "charity",
                  value: statistics?.campaignsByGoal["1"] || 0,
                },
                {
                  label: "zakat",
                  value: statistics?.campaignsByGoal["2"] || 0,
                  colorType: "secondary",
                },
                {
                  label: "endowments",
                  value: statistics?.campaignsByGoal["3"] || 0,
                  colorType: "tertiary",
                },
              ]}
              chartData={{
                labels: [t("charity"), t("zakat"), t("endowments")],
                data: [
                  statistics?.campaignsByGoal["1"] || 0,
                  statistics?.campaignsByGoal["2"] || 0,
                  statistics?.campaignsByGoal["3"] || 0,
                ],
                colors: ["#0d4ea2", "#5a9ef7", "#a6cfff"],
              }}
            />
          </Col>


          <Col xl={8} lg={12} xs={24}>
            <Card
              toolTip="total_donations_count"
              title="total_donations_count"
              icon={<FaDonate />}
              count={statistics?.donationsCount}
              details={[
                {
                  label: "periodic_donations_count",
                  value: statistics?.periodicDonationsCount || 0,
                },
                {
                  label: "non_periodic_donations_count",
                  value: statistics?.nonPeriodicDonationsCount || 0,
                  colorType: "secondary",
                },
              ]}
              chartData={{
                labels: [t("periodic"), t("non_periodic")],
                data: [
                  statistics?.periodicDonationsCount || 0,
                  statistics?.nonPeriodicDonationsCount || 0,
                ],
                colors: ["#0d4ea2", "#5a9ef7"],
              }}
            />
          </Col>

          <Col xl={8} lg={12} xs={24}>
            <Card
              toolTip="donations_by_status"
              title="donations_by_status"
              icon={<MdPayment />}
              count={statistics?.donationsByStatus["1"]}
              details={[
                {
                  label: "paid",
                  value: statistics?.donationsByStatus["1"] || 0,
                },
                {
                  label: "unpaid",
                  value: statistics?.donationsByStatus["2"] || 0,
                  colorType: "secondary",
                },
                {
                  label: "rejected",
                  value: statistics?.donationsByStatus["3"] || 0,
                  colorType: "tertiary",
                },
              ]}
              chartData={{
                labels: [t("paid"), t("unpaid"), t("rejected")],
                data: [
                  statistics?.donationsByStatus["1"] || 0,
                  statistics?.donationsByStatus["2"] || 0,
                  statistics?.donationsByStatus["3"] || 0,
                ],
                colors: ["#0d4ea2", "#5a9ef7", "#a6cfff"],
              }}
            />
          </Col>
              <Col xl={8} lg={12} xs={24}>
                <Card
                  toolTip="donors_count"
                  title="donors_count"
                  icon={<FaHandsHelping />}
                  count={statistics?.DonorsCount}
                  details={[
                    {
                      label: "total_donors",
                      value: statistics?.DonorsCount || 0,
                    },
                  ]}
                />
              </Col>
    
              <Col xl={8} lg={12} xs={24}>
                <Card
                  toolTip="orphans_count"
                  title="orphans_count"
                  icon={<FaHandsHelping />}
                  count={statistics?.orphanCount}
                  details={[
                    {
                      label: "total_orphans",
                      value: statistics?.orphanCount || 0,
                      colorType: "secondary",
                    },
                  ]}
                />
              </Col>
        </Row>

        <Divider orientation="center">{t("statistics")}</Divider>
      </Spin>
    </>
  );
};

export default Home;
